import { HStack, IconButton, Spacer, StackProps } from '@chakra-ui/react';
import { EditorState, RichUtils } from 'draft-js';
import * as React from 'react';

import {
  BoldIcon,
  ItalicIcon,
  ListIcon,
  UnderlineIcon,
} from '@/imports/ui/chakra/feather';
import { NumberedListIcon } from '@/imports/ui/components/TextEditor/components/Toolbar/components/NumberedListIcon';
import { AddLinkButton } from '@/modules/draft-js-link-plugin';

import { ImageAdd } from './components/ImageAdd';
import { TextSelector } from './components/TextSelector';
import { VideoAdd } from './components/VideoAdd';

export const Toolbar: React.FC<{
  editorState: EditorState;
  setEditorState: (editorState: EditorState) => void;
  focusEditor?: () => void;
}> = ({ editorState, setEditorState, focusEditor }) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const blockType = contentState
    .getBlockForKey(selection.getStartKey())
    .getType();

  const currentStyle = editorState.getCurrentInlineStyle();

  const toggleBlockType = (newBlockType: string, setBold: boolean) => {
    const newBlock = RichUtils.toggleBlockType(editorState, newBlockType);
    if (setBold) {
      setEditorState(RichUtils.toggleInlineStyle(newBlock, 'BOLD'));
    } else {
      setEditorState(newBlock);
    }
    focusEditor && focusEditor();
  };

  return (
    <HStack w="full">
      <TextSelector blockType={blockType} toggleBlockType={toggleBlockType} />
      <IconButton
        variant="outline"
        colorScheme="gray"
        isActive={currentStyle.has('BOLD')}
        icon={<BoldIcon />}
        aria-label="bold"
        //https://github.com/facebook/draft-js/issues/696#issuecomment-302903086
        onMouseDown={(event) => {
          event.preventDefault();
          setEditorState(RichUtils.toggleInlineStyle(editorState, 'BOLD'));
        }}
      />
      <IconButton
        variant="outline"
        colorScheme="gray"
        isActive={currentStyle.has('ITALIC')}
        icon={<ItalicIcon />}
        aria-label="italic"
        onMouseDown={(event) => {
          event.preventDefault();
          setEditorState(RichUtils.toggleInlineStyle(editorState, 'ITALIC'));
        }}
      />
      <IconButton
        variant="outline"
        colorScheme="gray"
        isActive={currentStyle.has('UNDERLINE')}
        icon={<UnderlineIcon />}
        aria-label="underline"
        onMouseDown={(event) => {
          event.preventDefault();
          setEditorState(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'));
        }}
      />
      <IconButton
        variant="outline"
        colorScheme="gray"
        isActive={blockType === 'ordered-list-item'}
        icon={<NumberedListIcon />}
        aria-label="ordered list"
        onMouseDown={(event) => {
          event.preventDefault();
          toggleBlockType('ordered-list-item', false);
        }}
      />
      <IconButton
        variant="outline"
        colorScheme="gray"
        isActive={blockType === 'unordered-list-item'}
        icon={<ListIcon />}
        aria-label="unordered list"
        onMouseDown={(event) => {
          event.preventDefault();
          toggleBlockType('unordered-list-item', false);
        }}
      />
      <AddLinkButton
        variant="outline"
        colorScheme="gray"
        editorState={editorState}
        setEditorState={setEditorState}
        aria-label="link"
      />
      <Spacer />
      <ImageAdd editorState={editorState} onChange={setEditorState} />
      <VideoAdd editorState={editorState} onChange={setEditorState} />
    </HStack>
  );
};
